import React, {useCallback, useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Modal} from "../../components";
import {AiFillWindows} from "react-icons/ai";
import {ThemeContext} from "../../contexts/ThemeContext";
import OneButtonLogo from "../../assets/img/logos/one_button_logo.png";
import OneDrawLogo from "../../assets/img/logos/one_draw_logo.png";
import OneDrawLogoWhite from "../../assets/img/logos/one_draw_logo_white.png";
import TrinityLogo from "../../assets/img/logos/Trinity_Solar_Logo_2025_-_Reversed_Full_Color_-_Web.svg";
import AuthGradient from "../../assets/img/background-gradients/bg-gradient.png";
import AuthPattern from "../../assets/img/background-gradients/bg-pattern.png";
import getMsalInstance, {loginRequest} from "../../helpers/getMsalInstance";
import {Button} from "antd";
import {useViewerStore} from "../../store/store";
import {logger} from "../../services/loggingService";
import {useIsAuthenticated} from "@azure/msal-react";
import {useCurrentUser} from "../../contexts/UserContext";
import type {PublicClientApplication} from "@azure/msal-browser";

interface UserLoginSuccessEvent extends CustomEvent<{ redirect: boolean; validated: boolean }> {
}

const LoginPage: React.FC = () => {
    const msalInstance: PublicClientApplication = getMsalInstance();
    const {theme} = useContext(ThemeContext);
    const {hasCapability} = useCurrentUser();
    const navigate = useNavigate();

    const [showModal, setShowModal] = useState<boolean>(false);
    const [isLoginProcessing, setIsLoginProcessing] = useState<boolean>(false);
    const isAzureAuthenticated = useIsAuthenticated();

    const closeModal = useCallback(() => {
        msalInstance.logoutRedirect();
        setShowModal(false);
    }, [msalInstance]);

    const handleUserLoginSuccess = useCallback(
        (event: Event) => {
            const customEvent = event as UserLoginSuccessEvent;
            if (customEvent.detail?.redirect && customEvent.detail?.validated) {
                useViewerStore.setState({isUserRedirectedToSiteAfterLogin: true});
                navigate("/sites");
            }
        },
        [navigate]
    );

    const handleUserHasNoEntitlement = useCallback(() => {
        setShowModal(true);
        sessionStorage.clear();
        localStorage.clear();
    }, []);

    useEffect(() => {
        window.addEventListener("userLoginSuccess", handleUserLoginSuccess);
        window.addEventListener("userHasNoEntitlement", handleUserHasNoEntitlement);

        return () => {
            window.removeEventListener("userLoginSuccess", handleUserLoginSuccess);
            window.removeEventListener("userHasNoEntitlement", handleUserHasNoEntitlement);
        };
    }, [handleUserLoginSuccess, handleUserHasNoEntitlement]);

    const handleSignIn = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        sessionStorage.setItem("loginMessage", "Login Successful, Redirecting...");
        setIsLoginProcessing(true);

        try {
            msalInstance.loginRedirect(loginRequest);
        } catch (error) {
            logger.error(error);
            setIsLoginProcessing(false);
        }
    };

    const year = new Date().getFullYear();
    const oneDrawMarkSrc = theme === "dark" ? OneDrawLogoWhite : OneDrawLogo;
    const showOneButton = hasCapability("Show_OneButton") || sessionStorage.getItem("showOneButtonBranding") === "1";

    return (
        <div className="app__layout auth">
            <section className="auth">
                <div className="auth__bg">
                    <img src={AuthGradient} alt="auth_gradient"/>
                </div>
                <div className="auth__bg bg-pattern">
                    <img src={AuthPattern} alt="auth_pattern"/>
                </div>

                <div className="auth__content">
                    <div className="auth__header">
                        <div className="auth__brand">
                            <img
                                src={TrinityLogo}
                                alt="Trinity Solar"
                                className="auth__brand-trinity"
                                style={{ height: 32, objectFit: "contain" }}
                            />
                            {showOneButton && (
                                <span
                                    className="auth__brand-badge"
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                        paddingInline: 10,
                                    }}
                                >
                                    <img
                                        src={OneButtonLogo}
                                        alt="oneBUTTON"
                                        className="auth__brand-logo"
                                        style={{
                                            height: 24,
                                            objectFit: "contain",
                                            backgroundColor: "#ffffff",
                                            borderRadius: 999,
                                            padding: "2px 10px",
                                        }}
                                    />
                                    <span
                                        className="auth__brand-pro"
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            padding: "2px 6px",
                                            borderRadius: 999,
                                            backgroundColor: "rgba(15,23,42,0.9)",
                                            color: "#facc15",
                                            letterSpacing: 0.6,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        Pro
                                    </span>
                                </span>
                            )}
                            <span className="auth__brand-powered">
                                {showOneButton && (
                                    <span className="auth__brand-powered-text">powered by</span>
                                )}
                                <img src={oneDrawMarkSrc} alt="oneDRAW" className="auth__brand-onedraw" />
                            </span>
                        </div>
                    </div>
                    <p className="auth__text">Sign in securely with your Microsoft account</p>

                    {isLoginProcessing ? (
                        <button className="auth__btn" disabled>
                            <AiFillWindows/>
                            Redirecting...
                        </button>
                    ) : (
                        <button
                            className="auth__btn"
                            disabled={isAzureAuthenticated}
                            onClick={handleSignIn}
                        >
                            <AiFillWindows/>
                            {sessionStorage.getItem("loginMessage") ??
                                "Login with Microsoft"}
                        </button>
                    )}
                </div>

                <div className="auth__footer">
                    <p className="auth__footer--copy">
                        © Trinity Solar, Inc. {year}. All Rights Reserved.
                    </p>
                </div>
            </section>

            {showModal && (
                <Modal title="Unauthorized Access!" messageType="error" close={closeModal}>
                    This account does not have access to oneDRAW. Please logout and try with a
                    different account or contact your administrator for permission to access
                    the application.
                    <br/>
                    <br/>
                    When you close this message, you will be asked to log out of your
                    account. Kindly sign out and try again.
                    <Button
                        type="primary"
                        onClick={closeModal}
                        style={{marginTop: "20px", background: "indianred"}}
                    >
                        Close and Logout
                    </Button>
                </Modal>
            )}
        </div>
    );
};

export default LoginPage;